import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, LineChart, Sparkles, ShieldCheck, Bot, TrendingUp, TrendingDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Ticker seed data (visual only — animates independently of the real feed)
const TICKER_SEED = [
  { s: "EUR/USD", p: 1.08543, d: +0.24 },
  { s: "GBP/USD", p: 1.27231, d: -0.11 },
  { s: "USD/JPY", p: 149.517, d: +0.38 },
  { s: "AUD/USD", p: 0.66201, d: +0.09 },
  { s: "USD/CAD", p: 1.35812, d: -0.05 },
  { s: "USD/CHF", p: 0.88214, d: +0.14 },
  { s: "NZD/USD", p: 0.61483, d: -0.22 },
  { s: "SPX 500", p: 5321.12, d: +0.62 },
  { s: "BTC/USD", p: 68432.5, d: +1.85 },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  // Recalc a few of the ticker prices every second (client-side only)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1200);
    return () => clearInterval(id);
  }, []);

  const ticker = useMemo(() => {
    return TICKER_SEED.map((t, i) => {
      const nudge = Math.sin((tick + i) * 0.7) * (t.p * 0.0004);
      const dir = Math.sin((tick + i) * 0.7);
      return {
        ...t,
        p: t.p + nudge,
        d: t.d + (dir > 0 ? 0.02 : -0.02),
      };
    });
  }, [tick]);

  // Animated candlestick sequence (rotates every tick)
  const candles = useMemo(() => {
    const arr = [];
    let last = 50;
    for (let i = 0; i < 14; i++) {
      const drift = Math.sin((tick + i) * 0.9) * 4 + (Math.random() - 0.5) * 3;
      const open = last;
      const close = Math.max(15, Math.min(85, open + drift));
      const high = Math.max(open, close) + Math.random() * 4;
      const low = Math.min(open, close) - Math.random() * 4;
      arr.push({ open, close, high, low, x: 12 + i * 15 });
      last = close;
    }
    return arr;
  }, [tick]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      toast.success(`Welcome, ${res.user.full_name}`);
      navigate(res.role === "admin" ? "/admin/dashboard" : "/client/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      {/* Scrolling ticker across the top */}
      <div className="border-b border-subtle bg-background overflow-hidden relative h-10 shrink-0" data-testid="login-ticker">
        <div className="absolute inset-y-0 flex items-center whitespace-nowrap animate-marquee font-mono-num text-xs">
          {[...ticker, ...ticker, ...ticker].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 px-6">
              <span className="text-muted-foreground">{t.s}</span>
              <span className="text-foreground">{t.p.toFixed(t.s.includes("BTC") ? 1 : t.s === "USD/JPY" ? 3 : 5)}</span>
              <span className={t.d >= 0 ? "text-profit inline-flex items-center gap-0.5" : "text-loss inline-flex items-center gap-0.5"}>
                {t.d >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {t.d >= 0 ? "+" : ""}{t.d.toFixed(2)}%
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 flex">
        <div className="w-full lg:w-[460px] flex flex-col justify-between px-8 lg:px-12 py-10 border-r border-subtle bg-background">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary flex items-center justify-center rounded">
              <LineChart className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono-num text-[10px] tracking-[0.2em] text-muted-foreground">TERMINAL</div>
              <div className="font-bold text-lg leading-none tracking-tight">XM FOREX</div>
            </div>
          </div>

          <div className="max-w-sm">
            <p className="label-eyebrow mb-2">Authentication</p>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Sign in to your terminal.</h1>
            <p className="text-sm text-muted-foreground mb-8">AI-driven forex execution with institutional-grade infrastructure.</p>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="label-eyebrow">Email</Label>
                <Input id="email" data-testid="login-email" type="email" placeholder="you@xmforex.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-11 bg-card border-subtle" autoComplete="email" />
              </div>
              <div>
                <Label htmlFor="password" className="label-eyebrow">Password</Label>
                <div className="relative mt-2">
                  <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 bg-card border-subtle pr-10" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility" aria-label="Toggle password visibility">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="login-submit">
                {loading ? "Authenticating…" : "Sign In to Terminal"}
              </Button>
            </form>
          </div>

          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} XM FOREX. All rights reserved.</p>
        </div>

        {/* Right — AI/trading marketing with rich moving graphics */}
        <div className="hidden lg:flex flex-1 relative overflow-hidden bg-card">
          <div className="absolute inset-0 bg-grid opacity-30" />
          {/* Animated glow orbs */}
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-primary/20 blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />

          {/* Rotating outer ring */}
          <svg className="absolute -right-40 top-1/2 -translate-y-1/2 opacity-25" width="800" height="800" viewBox="0 0 800 800">
            <g className="animate-spin-slow" style={{ transformOrigin: "400px 400px" }}>
              <circle cx="400" cy="400" r="380" fill="none" stroke="#3b82f6" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 12" />
              <circle cx="400" cy="400" r="340" fill="none" stroke="#3b82f6" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 20" />
            </g>
            <g className="animate-spin-reverse" style={{ transformOrigin: "400px 400px" }}>
              <circle cx="400" cy="400" r="300" fill="none" stroke="#16a34a" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="8 8" />
            </g>
          </svg>

          {/* Neural network SVG */}
          <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 800 600" fill="none" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="gline" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#16a34a" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {[
              [80, 120, 240, 200], [80, 300, 240, 200], [80, 480, 240, 400],
              [240, 200, 400, 140], [240, 200, 400, 300], [240, 400, 400, 300], [240, 400, 400, 460],
              [400, 140, 560, 220], [400, 300, 560, 220], [400, 300, 560, 380], [400, 460, 560, 380],
              [560, 220, 720, 300], [560, 380, 720, 300],
            ].map(([x1, y1, x2, y2], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#gline)" strokeWidth="1.2" strokeOpacity="0.7">
                <animate attributeName="stroke-opacity" values="0.2;0.9;0.2" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.15}s`} />
              </line>
            ))}
            {[
              [80, 120], [80, 300], [80, 480],
              [240, 200], [240, 400],
              [400, 140], [400, 300], [400, 460],
              [560, 220], [560, 380],
              [720, 300],
            ].map(([cx, cy], i) => (
              <g key={i}>
                <circle cx={cx} cy={cy} r="14" fill="#0b0b0e" stroke="#3b82f6" strokeOpacity="0.5" strokeWidth="1" />
                <circle cx={cx} cy={cy} r="4" fill="#3b82f6">
                  <animate attributeName="r" values="3;6;3" dur={`${2 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.2}s`} />
                </circle>
              </g>
            ))}
          </svg>

          {/* Live-morphing candlestick chart */}
          <div className="absolute right-20 top-24">
            <svg width="240" height="130" viewBox="0 0 240 130">
              {candles.map((c, i) => {
                const up = c.close >= c.open;
                const color = up ? "#16a34a" : "#dc2626";
                const bodyTop = Math.min(c.open, c.close);
                const bodyH = Math.max(2, Math.abs(c.close - c.open));
                return (
                  <g key={i} style={{ transition: "all 0.6s ease-out" }}>
                    <line x1={c.x} x2={c.x} y1={c.high} y2={c.low} stroke={color} strokeWidth="1" style={{ transition: "all 0.6s ease-out" }} />
                    <rect x={c.x - 3} y={bodyTop} width="6" height={bodyH} fill={color} style={{ transition: "all 0.6s ease-out" }} />
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Floating currency symbols */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {["$", "€", "£", "¥", "₹", "₿"].map((sym, i) => (
              <div
                key={i}
                className="absolute font-mono-num text-2xl text-primary/20 animate-float"
                style={{
                  left: `${15 + i * 14}%`,
                  top: `${20 + (i % 3) * 20}%`,
                  animationDelay: `${i * 0.9}s`,
                  animationDuration: `${8 + (i % 3) * 2}s`,
                }}
              >
                {sym}
              </div>
            ))}
          </div>

          <div className="relative z-10 flex flex-col justify-center px-16 max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              <span className="label-eyebrow text-primary">AI-Powered · Institutional Grade</span>
            </div>
            <h2 className="text-5xl xl:text-6xl font-bold tracking-tighter leading-[0.95] mb-6">
              The future of<br />
              <span className="text-primary">forex trading</span> is intelligent.
            </h2>
            <p className="text-base text-muted-foreground max-w-md mb-12">
              Deep-learning signal engine, sub-millisecond execution, and real-time market intelligence — engineered for traders who demand more.
            </p>

            <div className="grid grid-cols-3 gap-px bg-border max-w-2xl">
              {[
                { icon: Bot, k: "Signal Engine", v: "Neural" },
                { icon: LineChart, k: "Execution", v: "< 5ms" },
                { icon: ShieldCheck, k: "Security", v: "AES-256" },
              ].map(({ icon: Icon, k, v }) => (
                <div key={k} className="bg-background px-6 py-5">
                  <Icon className="w-4 h-4 text-primary mb-2" />
                  <div className="label-eyebrow mb-1">{k}</div>
                  <div className="font-mono-num text-2xl font-semibold">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
